/**
 * Redux action creators for helpdesk ticket fetching.
 *
 * @module redux/helpdesk/actions
 * @author Snowfly
 * @version 1.0.0
 */
import {FETCH_HELPDESK_TICKETS} from "./helpdeskTypes";
import {FETCH_FAILURE} from "../sims/simTypes";
import axios from "axios";
import config from "../../config";
import {fetchRequest} from "../sims/actions";
import helpdeskPermissions from "../../permissions/helpdesk";

/**
 * Action creator for handling fetch failure events.
 *
 * This function generates an action object to represent a failure
 * state during a data fetch operation. The action object contains
 * a type property and the associated payload.
 *
 * @param {any} data - The payload data representing details of the fetch failure,
 *                     such as error information or failure reason.
 * @returns {Object} An action object with a type of `FETCH_FAILURE` and the provided payload.
 */
export const fetchFailure = (data) => ({
    type: FETCH_FAILURE,
    payload: data,
});

/**
 * An action creator function that generates an action object for fetching helpdesk tickets.
 *
 * @param {Object} data - The payload containing the necessary information for fetching helpdesk tickets.
 * @returns {Object} An action object with a `type` property set to `FETCH_HELPDESK_TICKETS`
 * and a `payload` property containing the provided data.
 */
export const fetchHelpdeskTicket = (data) => ({
    type: FETCH_HELPDESK_TICKETS,
    payload: data,
});

/**
 * Fetches help desk tickets from the API based on the provided criteria.
 *
 * @param {number} [page=0] - The page number for pagination. Defaults to 0 if not provided.
 * @param {string|null} [textSearch=null] - The search query for filtering tickets by text. Defaults to null.
 * @param {string|null} [orderBy=null] - The field by which the tickets should be ordered. Defaults to 'createdAt'.
 * @param {string|null} [order=null] - The order direction, either 'asc' or 'desc'. Defaults to 'asc'.
 * @param {Object|null} [filter=null] - Additional filters to apply to the ticket list. Defaults to null.
 * @returns {Function} A thunk function that handles the asynchronous API request and dispatches actions based on the result.
 */
export const fetchHelpdeskTickets = (
    page = 0, textSearch = null, orderBy = null, order = null, filter = null
) => async (dispatch) => {
    dispatch(fetchRequest());
    let task = helpdeskPermissions.tickets.view.task;
    let type = helpdeskPermissions.tickets.view.type;
    await axios.post(
        `${config.API_BASE_URL}/api/v1/help-desk/tickets/list?page=${page ?? 0}&orderBy=${orderBy ?? 'createdAt'}&order=${order ?? 'asc'}`,
        {
            page: page,
            textSearch: textSearch,
            orderBy: orderBy,
            order: order,
            filters: filter,
        },
        {
            headers: {
                Authorization: config.getToken(),
                task,
                type,
            }
        }
    ).then((res) => dispatch(fetchHelpdeskTicket(res)))
        .catch((error) => dispatch(fetchFailure(error)));
};
